import React, { useEffect, useRef, useState } from 'react';

import {
    IonCard,
    IonCardContent,
    IonCol,
    IonContent,
    IonGrid,
    IonLabel,
    IonNote,
    IonRow,
    IonTitle,
    IonicSlides,
} from '@ionic/react';
import '@ionic/react/css/ionic-swiper.css';
import { filter, find, keys, map, maxBy, pick, size, toInteger } from 'lodash/fp';
import { DateTime } from 'luxon';
import 'swiper/css';
import 'swiper/css/effect-cards';
import 'swiper/css/keyboard';
import 'swiper/css/pagination';
import { EffectCreative, Keyboard, Pagination } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import './ManualPointing.scss';

import { Header } from '../components/Header';
import PointingDetail, { ViewMode } from '../components/common/PointingDetail';
import { OdooPointing, Pointing, pointingFields } from '../types/models';
import odoo, { standardizedOdooPointings } from '../utils/odoo';
import { PointingState } from '../utils/odooUtils';

const keyOfPointingToPick = [
    'input_time_start',
    'input_time_end',
    'lunch_night',
    'lunch',
    'mad_id',
    'other_workplace',
    'city',
    'other_mad',
    'travel',
    'absence_hours',
    'working_hours',
    'working_night_hours',
    'bad_weather_hours',
    'workplace_id',
];
export const getFieldsChanged = (previousPointing: Pointing | undefined, nextPointing: Pointing | undefined) => {
    if (!previousPointing || !nextPointing) {
        return [];
    }
    return filter(
        (key) =>
            previousPointing[key] instanceof DateTime && nextPointing[key] instanceof DateTime
                ? !(previousPointing[key] as DateTime).hasSame(nextPointing[key] as DateTime, 'day')
                : previousPointing[key] !== nextPointing[key],
        keys(pick(keyOfPointingToPick, previousPointing)) as Array<keyof Partial<Pointing>>,
    );
};

export const getLastPointingByState = (state: PointingState, pointings: Pointing[], originId: number) =>
    maxBy(
        'id',
        filter((pointing) => pointing.state === state && pointing.origin_id === toInteger(originId), pointings),
    ) || find((pointing) => pointing.state === state && pointing.origin_id === 0, pointings);

const History: React.FC<{
    pointingId: number;
    pointingsHistory: Pointing[];
    viewMode: ViewMode;
    isEmbedded?: boolean;
    dismiss?: () => void;
}> = ({ pointingId, pointingsHistory, viewMode, isEmbedded = false, dismiss }) => {
    const title = 'Historique';

    const swiperRef = useRef<SwiperRef>(null);

    const getLastPointingByStateCurried = (state: PointingState) =>
        getLastPointingByState(state, pointingsHistory, pointingId);

    const employeeLastPointing = getLastPointingByStateCurried(PointingState.WaitValidation);
    const adherentLastPointing = getLastPointingByStateCurried(PointingState.AdherentValidation);
    const geiqLastPointing = getLastPointingByStateCurried(PointingState.GeiqValidation);
    const pointingToShow = filter('id', [
        employeeLastPointing,
        { ...adherentLastPointing, fieldsChanged: getFieldsChanged(employeeLastPointing, adherentLastPointing) },
        { ...geiqLastPointing, fieldsChanged: getFieldsChanged(adherentLastPointing, geiqLastPointing) },
    ]) as (Pointing & { fieldsChanged: string[] })[];

    return (
        <>
            {!isEmbedded && <Header title={title} backText={'Retour'} />}
            <IonContent fullscreen={true}>
                {size(pointingToShow) > 0 ? (
                    <div className="relative pb-12">
                        <Swiper
                            slidesPerView={1}
                            spaceBetween={30}
                            ref={swiperRef}
                            onInit={(swiper) => {
                                swiper.slideTo(pointingToShow.length - 1);
                            }}
                            grabCursor={true}
                            pagination={{
                                clickable: true,
                                dynamicBullets: true,
                            }}
                            keyboard={{
                                pageUpDown: true,
                            }}
                            modules={[EffectCreative, Pagination, Keyboard, IonicSlides]}
                        >
                            {map(
                                (pointing) => (
                                    <SwiperSlide key={`${pointing.date}_${pointing.id}`}>
                                        <IonCard className="mt-5 mx-4 bg-white history-card-content-d">
                                            <IonCardContent className="px-0">
                                                <IonGrid className="text-left text-black text-xs">
                                                    <IonRow>
                                                        <IonCol size="6">
                                                            <IonLabel>Pointage du : </IonLabel>
                                                            <IonNote>
                                                                {DateTime.fromISO(pointing.date).toFormat('dd/MM/yyyy')}
                                                            </IonNote>
                                                        </IonCol>
                                                        <IonCol>
                                                            <IonLabel>Version : </IonLabel>
                                                            <IonNote>
                                                                {pointing.state === PointingState.WaitValidation
                                                                    ? 'Employée'
                                                                    : pointing.state ===
                                                                        PointingState.AdherentValidation
                                                                      ? 'Adhérent'
                                                                      : 'GEIQ'}
                                                            </IonNote>
                                                        </IonCol>
                                                    </IonRow>
                                                    {(pointing.state === PointingState.AdherentValidation ||
                                                        pointing.state === PointingState.GeiqValidation) && (
                                                        <IonRow>
                                                            <IonCol size="6">
                                                                <IonLabel>Validé le : </IonLabel>
                                                                <IonNote>
                                                                    {(
                                                                        pointing.user_geiq_validation_date ||
                                                                        pointing.user_adherent_validation_date
                                                                    )?.toFormat('dd/MM/yyyy')}
                                                                </IonNote>
                                                            </IonCol>
                                                            <IonCol size="auto">
                                                                <IonLabel>par : </IonLabel>
                                                                <IonNote>
                                                                    {(pointing.user_geiq_id &&
                                                                        pointing.user_geiq_id[1]) ||
                                                                        (pointing.user_adherent_id &&
                                                                            pointing.user_adherent_id[1])}
                                                                </IonNote>
                                                            </IonCol>
                                                        </IonRow>
                                                    )}
                                                </IonGrid>
                                            </IonCardContent>
                                        </IonCard>
                                        <PointingDetail
                                            initialPointing={pointing}
                                            isDisabled={true}
                                            onChange={() => {}}
                                            viewMode={viewMode}
                                            fieldsToHighlight={pointing.fieldsChanged}
                                        />
                                    </SwiperSlide>
                                ),
                                pointingToShow,
                            )}
                        </Swiper>
                    </div>
                ) : (
                    <div className="grid h-[50vh] place-items-center">
                        <IonTitle className="text-center text-gray-500">Aucun historique</IonTitle>
                    </div>
                )}
            </IonContent>
        </>
    );
};

export const HistoryStandalone: React.FC<{
    pointingId: number;
    viewMode?: ViewMode;
    dismiss?: () => void;
}> = ({ pointingId, viewMode = ViewMode.Confirm, dismiss }) => {
    const [pointingsHistory, setPointingsHistory] = useState<Pointing[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPointings = async () => {
            try {
                const pointings: OdooPointing[] = await odoo.searchRead('hr.madper.app.pointing.time', {
                    fields: pointingFields,
                    domain: [['origin_id', '=', pointingId]],
                });
                setPointingsHistory(standardizedOdooPointings(pointings));
            } catch (error) {
                console.error('Error fetching pointings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPointings();
    }, [pointingId]);

    if (isLoading) {
        return <div>Chargement...</div>;
    }

    return (
        <History pointingId={pointingId} pointingsHistory={pointingsHistory} viewMode={viewMode} isEmbedded={true} />
    );
};

export default History;
